import { Row } from 'react-table';

const moneyToString = (str: string) => str.replace(/\D/g, '');

// A non-finite key (from `Number("null")` etc.) would make the comparator non-antisymmetric and
// scramble non-tied rows.
const toFinite = (n: number) => (Number.isFinite(n) ? n : 0);

const sortMoney = (rowA: Row, rowB: Row, columnId: string) => {
  const a = toFinite(Number(moneyToString(rowA.original[columnId] ?? '')));
  const b = toFinite(Number(moneyToString(rowB.original[columnId] ?? '')));
  return a - b;
};

const getJsxKey = (row: Row, columnId: string) => row.original?.[columnId]?.key;

// Natural, case-insensitive ordering ('TVN2' before 'TVN10'); plain `>` code-point compares
// scramble mixed-case titles and numeric tokens.
const naturalCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

const sortJsxKey = (rowA: Row, rowB: Row, columnId: string) => {
  const a = getJsxKey(rowA, columnId);
  const b = getJsxKey(rowB, columnId);
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return naturalCollator.compare(String(a), String(b));
};

const sortJsxKeyNumber = (rowA: Row, rowB: Row, columnId: string) => {
  const a = toFinite(Number(getJsxKey(rowA, columnId)));
  const b = toFinite(Number(getJsxKey(rowB, columnId)));
  return a - b;
};

// Factory: natural, case-insensitive sort keyed by the cell's React `key`, read via a captured
// accessor instead of the columnId react-table passes. The grouped optimization columns set
// `id = accessor + tableViewSwitch` (doubled), so the columnId never matches the row-data key;
// the columnId-based `jsxKey` therefore reads undefined and returns 0 for every pair, leaving the
// Label/Channel/Impact group/Attribution columns unsortable. Closing over the accessor fixes that,
// the same way `createSortByAccessor` already does for the funnel-step columns (id !== accessor).
const createSortByJsxKeyNatural = (accessor: string) => (rowA: Row, rowB: Row) => {
  const a = rowA.original?.[accessor]?.key;
  const b = rowB.original?.[accessor]?.key;
  // One nullish tier for "this row has no sortable cell", so the comparator stays antisymmetric: an
  // absent cell (`original[accessor]` undefined) and a cell rendered without a key must not both claim
  // to precede the other. `==` collapses both to the same bucket before the natural compare.
  // NB this tier cannot catch a null NAME: React coerces `key={null}` to the STRING "null" and only
  // `key={undefined}` to null, so a nullable name would sort as a group literally called "null" rather
  // than land here. The keys feeding this comparator are non-null in the schema; give a cell an explicit
  // `key={name ?? undefined}` if that ever changes.
  const aNull = a == null;
  const bNull = b == null;
  if (aNull && bNull) return 0;
  if (aNull) return -1;
  if (bNull) return 1;
  if (a === b) return 0;
  return naturalCollator.compare(String(a), String(b));
};

// Factory function to create a sorting function that uses a specific accessor
const createSortByAccessor = (accessor: string) => (rowA: Row, rowB: Row) => {
  const a = rowA.original[accessor]?.key;
  const b = rowB.original[accessor]?.key;

  if (a === undefined || b === undefined) return 0;
  const na = toFinite(Number(a));
  const nb = toFinite(Number(b));
  if (na === nb) return 0;
  return na < nb ? 1 : -1;
};

export const sortTypes = {
  money: sortMoney,
  jsxKey: sortJsxKey,
  jsxKeyAsNumber: sortJsxKeyNumber,
  createSortByAccessor,
  createSortByJsxKeyNatural,
};
